import { addTotals } from 'checkout/lib/utils/payments/totals'
import { sum } from 'lib/arrayUtils'
import createSelector from 'lib/createSelector'
import {
  getAccommodationPayable,
  getAccommodationTotals,
  selectEarnableBusinessTravellerCreditsTotal,
} from 'checkout/selectors/payment/accommodation'
import { getExperienceTotals, getTransferTotals } from 'checkout/selectors/payment/experience'
import { getFlightTotals } from 'checkout/selectors/payment/flights'
import { getInsuranceTotals } from 'checkout/selectors/payment/insurance'
import { checkoutAccommodationOfferView } from 'checkout/selectors/view/accommodation'
import { computeCruiseDepositAmount, computeDepositAmount } from 'checkout/lib/utils/payments/deposit'
import { getAccommodationDepositSettings } from 'checkout/lib/utils/accommodation/deposit'
import { getCarHireTotals } from './carHire'
import { getDefaultDepositAmountPercentage, getDepositServiceFeeConfig } from '../featureConfig/deposit'
import config from 'constants/config'
import { VIRGIN_VELOCITY_BURN_RATE } from 'constants/partnerships'
import { getBundleAndSaveTotals } from './bundleAndSave'
import { getGiftCardTotals } from 'checkout/selectors/payment/giftCard'
import { CHECKOUT_SAVING_PERCENTAGE_THRESHOLD } from 'constants/checkout'
import { isLastInstalment } from 'lib/payInInstalmentsUtils'
import { PAYMENT_OPTIONS } from 'constants/payment'

export const isUsingCredit = (state: App.State) => state.checkout.payment.useCredit
export const getPromo = (state: App.State) => state.checkout.promo
/**
 * getDiscountTotal returns the total dollar amount the promo code is worth for this order.
 *
 * Old Cart uses the clients-side logic below for the promo calculation
 * New Checkout uses Server-Side promo (or PromoV2) calculations via making a POST request to /api/promo/discount with a 'Discount Request Order'
 */
export const getDiscountTotal = createSelector(
  getPromo,
  (promotion) => {
    if (!promotion) {
      return 0
    }
    return promotion.discountTotal || 0
  },
)

export const getTotalsWithoutInsurance = createSelector(
  getAccommodationTotals,
  getBundleAndSaveTotals,
  getExperienceTotals,
  getTransferTotals,
  getFlightTotals,
  getCarHireTotals,
  getGiftCardTotals,
  selectEarnableBusinessTravellerCreditsTotal,
  (...itemTotals: Array<App.WithDataStatus<App.Checkout.ItemTotals>>): App.WithDataStatus<App.Checkout.ItemTotals> => {
    return addTotals(itemTotals)
  },
)

const getAncillaryPaymentTotals = createSelector(
  (state: App.State) => state.checkout.cart.postPurchase,
  (state: App.State) => state.checkout.cart.existingOrder,
  (postPurchaseMode, existingOrder): App.Checkout.ItemTotals | undefined => {
    if (existingOrder) {
      if (postPurchaseMode === 'deposit-balance' && existingOrder.depositDetails) {
        return {
          price: existingOrder.depositDetails.balance_amount,
          taxesAndFees: 0,
        }
      } else if (existingOrder.instalmentDetails && (postPurchaseMode === 'instalment-balance' || isLastInstalment(existingOrder.instalmentDetails))) {
        return {
          price: existingOrder.instalmentDetails.balance_amount,
          taxesAndFees: 0,
        }
      } else if (postPurchaseMode === 'instalment' && existingOrder.instalmentDetails) {
        return {
          price: existingOrder.instalmentDetails.per_instalment_amount,
          taxesAndFees: 0,
        }
      } else if (postPurchaseMode === 'reserve-balance' && existingOrder.reserveForZeroDetails) {
        return {
          price: existingOrder.reserveForZeroDetails.payable_amount,
          taxesAndFees: 0,
        }
      }
    }

    return
  },
)

export const getCheckoutTotals = createSelector(
  getTotalsWithoutInsurance,
  getInsuranceTotals,
  getAncillaryPaymentTotals,
  (totals, insurance, ancillaryTotals): App.WithDataStatus<App.Checkout.ItemTotals> => {
    return ancillaryTotals ? { hasRequiredData: true, data: ancillaryTotals } : addTotals([totals, insurance])
  },
)

export const getCheckoutTotal = createSelector(
  getCheckoutTotals,
  (checkoutTotals): number => {
    const { price, surcharge, otherFees } = checkoutTotals.data
    // need to adjust values to account for value possibly being undefined
    const adjPrice = price ?? 0
    const adjSurcharge = surcharge ?? 0
    const adjOtherFees = otherFees ? sum(Object.values(otherFees)) : 0
    return adjPrice + adjSurcharge + adjOtherFees
  },
)
export const getCheckoutTotalNoCruiseValidation = createSelector(
  getCheckoutTotals,
  (checkoutTotals): number => {
    const { price, surcharge, otherFees } = checkoutTotals.data
    // need to adjust values to account for value possibly being undefined
    const adjPrice = price ?? 0
    const adjSurcharge = surcharge ?? 0
    const adjOtherFees = otherFees ? sum(Object.values(otherFees)) : 0
    return adjPrice + adjSurcharge + adjOtherFees
  },
)

export const getDueAtProperty = createSelector(
  getCheckoutTotals,
  (checkoutTotals): number => {
    const { otherFees } = checkoutTotals.data
    return otherFees?.propertyFees || 0
  },
)

export const getPayableTotal = createSelector(
  getCheckoutTotal,
  getDueAtProperty,
  (total, dueAtProperty): number => total - dueAtProperty,
)

export const getPayInFull = createSelector(
  getCheckoutTotalNoCruiseValidation,
  getDueAtProperty,
  (total, dueAtProperty): number => total - dueAtProperty,
)

export const checkoutPromotionView = createSelector(
  (state: App.State) => state.checkout.promo,
  getDiscountTotal,
  (promotion, discount): App.Checkout.PromotionView => ({
    promotion,
    discount,
  }),
)

export const getPayableAfterDiscount = createSelector(
  getPayableTotal,
  getDiscountTotal,
  (payableTotal, discount): number => payableTotal - discount,
)

/**
 * Calculate total modifying amount for partnerships
 *
 * @remarks
 * Currently applicable only for:
 * - VIRGIN_VELOCITY
 */
export const getPartnershipModifierAmount = createSelector(
  (state: App.State) => state.checkout.payment.partnerships,
  (partnerships) => {
    let result = 0

    if (partnerships.velocity?.burn?.isApplied && partnerships.velocity?.burn?.pointsBurned) {
      result += Math.round(((partnerships.velocity.burn.pointsBurned * VIRGIN_VELOCITY_BURN_RATE) + Number.EPSILON) * 100) / 100
    }

    return result
  },
)

export const getPayableAfterModifiers = createSelector(
  getPayableTotal,
  getDiscountTotal,
  getPartnershipModifierAmount,
  (payableTotal, promoDiscountAmount, partnershipModifierAmount): number => payableTotal - promoDiscountAmount - partnershipModifierAmount,
)

/**
 * Keep this selector internal
 * An exported version can be found in:
 * {@link checkout/selectors/payment/deposit#getDepositAmountBeforeCreditApplied}
 *
 * @remark
 * We copy it because we cannot import it without a circular import problem
 * There is no easy way to untangle it either...
 */
const getDepositAmountBeforeCreditApplied = createSelector(
  getPayableTotal,
  getAccommodationPayable,
  checkoutAccommodationOfferView,
  getDefaultDepositAmountPercentage,
  (payableTotal, accommodationPayable, viewWithStatus, defaultDepositAmountPercentage): number => {
    const isCruise = viewWithStatus?.data[0]?.offerType === 'cruise'

    const depositSettings = viewWithStatus.hasRequiredData && viewWithStatus.data.length > 0 ?
      getAccommodationDepositSettings(viewWithStatus.data[0], defaultDepositAmountPercentage) :
      undefined
    const depositAmount = isCruise ?
      computeCruiseDepositAmount(accommodationPayable, depositSettings) :
      computeDepositAmount(accommodationPayable, depositSettings)

    return payableTotal - accommodationPayable + depositAmount
  },
)

const getServiceFeeAmount = createSelector(
  getPayableAfterModifiers,
  getDepositAmountBeforeCreditApplied,
  getDepositServiceFeeConfig,
  (payable, depositAmount, serviceFee) => {
    const depositBalance = payable - depositAmount
    const serviceFeeAmount = Number((serviceFee.percentage / 100 * depositBalance).toFixed(2))
    return serviceFeeAmount
  },
)

export const getCreditPayableAmount = createSelector(
  getPayableAfterModifiers,
  (state: App.State) => state.auth.account.creditsByCurrency[state.checkout.cart.currencyCode],
  (state: App.State) => state.auth.account.isSpoofed,
  getDepositAmountBeforeCreditApplied,
  (state: App.State) => state.cart.rebookingID,
  getServiceFeeAmount,
  (payable, credits, isSpoofed, depositAmount, rebookingID, serviceFee) => {
    let maxCreditTotal: number
    const creditBalance = credits?.balance ?? 0
    if (isSpoofed && rebookingID) {
      const rebookingPayable = depositAmount + serviceFee
      maxCreditTotal = creditBalance < rebookingPayable ? creditBalance : rebookingPayable
    } else {
      maxCreditTotal = Math.min(creditBalance, payable)
    }
    return maxCreditTotal
  },
)

export const getCreditPayAmount = createSelector(
  isUsingCredit,
  getCreditPayableAmount,
  (useCredit, amount) => useCredit ? amount : 0,
)

export const getMerchantFeeAmount = createSelector(
  getPayableTotal,
  getDiscountTotal,
  getDepositAmountBeforeCreditApplied,
  getCreditPayAmount,
  getPartnershipModifierAmount,
  (state: App.State) => state.checkout.merchantFeeConfigs,
  (state: App.State) => state.checkout.merchantFeePaymentType,
  (_state: App.State, paymentType: PAYMENT_OPTIONS) => paymentType,
  (paymentAmount, discount, depositAmount, creditAmount, partnershipModifierAmount, merchantFeeConfigs, merchantFeePaymentType, paymentType) => {
    if (paymentType === PAYMENT_OPTIONS.RESERVE_FOR_FREE) return 0
    if (!merchantFeePaymentType || merchantFeeConfigs.length === 0) return 0

    const merchantFeePercentage = merchantFeeConfigs.find((config) => config.id === merchantFeePaymentType)?.percentage
    if (!merchantFeePercentage) return 0

    let payableStripeTotal = 0

    if (paymentType === PAYMENT_OPTIONS.FULL) {
      payableStripeTotal = paymentAmount - discount - creditAmount - partnershipModifierAmount
    } else if (paymentType === PAYMENT_OPTIONS.DEPOSIT) {
      payableStripeTotal = depositAmount
    }

    const merchantFee = payableStripeTotal * (merchantFeePercentage / 100)
    const formattedMerchantFee = Math.round((merchantFee + Number.EPSILON) * 100) / 100
    return formattedMerchantFee
  },
)

export const checkoutTotalsView = createSelector(
  getCheckoutTotals,
  getCheckoutTotal,
  getPayableTotal,
  getDiscountTotal,
  getCreditPayableAmount,
  getCreditPayAmount,
  (state: App.State) => state.checkout.payment.partnerships.velocity,
  getPartnershipModifierAmount,
  (state: App.State) => getMerchantFeeAmount(state, state.checkout.payment.paymentSelected),
  (state: App.State) => state.checkout.payment.paymentSelected,
  getServiceFeeAmount,
  (
    checkoutTotals,
    total,
    payableTotal,
    discount,
    amountPayableByCredit,
    creditPaymentAmount,
    velocityState,
    partnershipModifierAmount,
    merchantFee,
    paymentSelected,
    serviceFee,
  ): App.WithDataStatus<App.Checkout.TotalsView> => {
    const {
      price,
      value,
      surcharge,
      taxesAndFees,
      otherFees,
      propertiesFees,
      businessTravellerCredits,
      carHirePaidOnPickUpFees,
      paidPrice,
      newPrice,
    } = checkoutTotals.data

    // need to adjust values to account for value possibly being undefined
    const adjPrice = price ?? 0
    const adjSurcharge = surcharge ?? 0
    const adjValue = value ?? 0
    const adjCarHirePaidOnPickUpFees = carHirePaidOnPickUpFees ?? 0

    const subTotal = adjPrice + adjSurcharge - taxesAndFees + adjCarHirePaidOnPickUpFees
    const totalValue = adjValue + adjSurcharge

    // Value always includes `taxesAndFees`
    const savedPercentage = Math.floor(((totalValue - (subTotal + taxesAndFees)) / totalValue) * 100)

    const payableServiceFee = paymentSelected === PAYMENT_OPTIONS.DEPOSIT ? serviceFee : 0

    const grandTotal = total -
      discount -
      creditPaymentAmount -
      partnershipModifierAmount +
      adjCarHirePaidOnPickUpFees +
      merchantFee +
      payableServiceFee

    const payableNow = payableTotal - discount - creditPaymentAmount - partnershipModifierAmount + merchantFee + payableServiceFee

    const businessTravellerCredit = config.businessTraveller.currentAccountMode === 'business' && businessTravellerCredits ?
        { totalCreditValue: businessTravellerCredits } :
      undefined

    const velocityPointsApplied = (velocityState?.burn && velocityState.burn.isApplied) ? velocityState.burn.pointsBurned : null

    return {
      hasRequiredData: checkoutTotals.hasRequiredData,
      data: {
        savedPercentage,
        savedTotal: totalValue - subTotal,
        amountPayableByCredit,
        creditPaymentAmount,
        otherFees,
        propertiesFees,
        taxesAndFees,
        subTotal,
        grandTotal,
        payableNow,
        value: totalValue,
        businessTravellerCredit,
        carHirePaidOnPickUpFees: adjCarHirePaidOnPickUpFees,
        ...(velocityPointsApplied && { velocityPointsApplied }),
        paidPrice,
        newPrice,
        merchantFee,
      },
    }
  },
)

export const getCheckoutTotalsForPaymentSchedule = createSelector(
  getPayableTotal,
  getDiscountTotal,
  getCreditPayAmount,
  getPartnershipModifierAmount,
  (state: App.State, paymentType: PAYMENT_OPTIONS) => getMerchantFeeAmount(state, paymentType),
  (payableTotal, discountTotal, creditTotal, partnershipModifierAmount, merchantFee) =>
    payableTotal -
    discountTotal -
    creditTotal -
    partnershipModifierAmount +
    merchantFee,
)

export const showSavingValue = createSelector(
  checkoutTotalsView,
  (totalsView) => {
    const accountMode = config.businessTraveller.currentAccountMode
    const { savedPercentage, businessTravellerCredit } = totalsView.data
    const hasBusinessCreditsBanner = accountMode === 'business' && !!businessTravellerCredit?.totalCreditValue
    return !hasBusinessCreditsBanner && savedPercentage >= CHECKOUT_SAVING_PERCENTAGE_THRESHOLD
  },
)
